import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { UserRole } from '../types';

const NEVER_KEY = (userId: string) => `welcome_never_${userId}`;
const SESSION_KEY = (userId: string) => `welcome_shown_session_${userId}`;

interface Step {
  icon: string;
  title: string;
  description: string;
  tip?: string;
}

const POOL_STEPS: Step[] = [
  {
    icon: '🏊',
    title: 'Havuz Sistemi',
    description: 'Yöneticiniz transferleri havuza bırakır. Havuz üyeleri aynı anda bu transferleri görür — ilk "Seç" diyen kazanır.',
    tip: 'Yeni transfer düştüğünde e-posta bildirimi alırsın.',
  },
  {
    icon: '📋',
    title: 'Transferleri Takip Et',
    description: '"Transferlerim" sayfasında mevcut ve kabul ettiğin transferleri görürsün. Güzergah, tarih ve ücreti karşılaştırarak seçim yapabilirsin.',
    tip: 'Transfere tıkla → detayları gör → "Seç" butonuna bas.',
  },
  {
    icon: '💰',
    title: 'Ödemelerini Gör',
    description: 'Tamamladığın transferlerin ödemeleri "Ödemelerim" bölümünde listelenir. Bakiyeni ve geçmiş işlemlerini buradan takip edebilirsin.',
    tip: 'Ödeme yöneticiniz tarafından onaylandıktan sonra görünür.',
  },
];

const USER_STEPS: Step[] = [
  {
    icon: '➕',
    title: 'Transfer Oluştur',
    description: '"Transferler → Yeni Transfer" ile tarih, saat, güzergah ve yolcu bilgilerini girerek saniyeler içinde transfer oluşturabilirsin.',
    tip: 'Toplu transfer yüklemek için Excel import özelliğini kullan.',
  },
  {
    icon: '🎯',
    title: 'Atama Yap',
    description: 'Transferi kendi sürücüne direkt atayabilir, anlaşmalı tedarikçiye gönderebilir ya da havuza bırakarak müsait üyenin almasını sağlayabilirsin.',
    tip: '"Tedarikçiler" ve "Sürücüler" menüsünden önceden ekle.',
  },
  {
    icon: '💳',
    title: 'Ödemeleri Takip Et',
    description: '"Ödemeler" menüsünden tedarikçi ve havuz üyelerine yapılan ödemeleri yönetebilirsin. Bekleyen ve tamamlanan ödemeler ayrı ayrı listelenir.',
    tip: 'Ödeme geçmişini Excel olarak dışa aktarabilirsin.',
  },
  {
    icon: '📊',
    title: 'Dashboard & Raporlar',
    description: 'Ana ekranda günlük transfer sayısı, toplam gelir ve net kâr anlık olarak görünür. Tüm veriler otomatik güncellenir.',
    tip: 'Tarih filtresiyle haftalık ve aylık kıyaslama yapabilirsin.',
  },
];

export const WelcomeModal: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [neverShow, setNeverShow] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    if (localStorage.getItem(NEVER_KEY(user.id))) return;
    if (sessionStorage.getItem(SESSION_KEY(user.id))) return;
    sessionStorage.setItem(SESSION_KEY(user.id), '1');
    setVisible(true);
  }, [user?.id]);

  const handleClose = () => {
    if (neverShow && user?.id) {
      localStorage.setItem(NEVER_KEY(user.id), '1');
    }
    setVisible(false);
  };

  if (!visible || !user) return null;

  const isPoolMember = user.role === UserRole.POOL_MEMBER;
  const steps = isPoolMember ? POOL_STEPS : USER_STEPS;
  const current = steps[step];
  const isLast = step === steps.length - 1;
  const firstName = user.fullName?.split(' ')[0] || '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 text-center">
          {step === 0 && (
            <p className="text-sm text-gray-400 mb-3">
              Hoş geldin{firstName ? `, ${firstName}` : ''}! 👋
            </p>
          )}
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-5">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`rounded-full transition-all ${
                  i === step
                    ? 'w-6 h-2 bg-blue-600'
                    : i < step
                    ? 'w-2 h-2 bg-blue-300'
                    : 'w-2 h-2 bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 pb-2 text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-4xl mx-auto mb-4">
            {current.icon}
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{current.title}</h2>
          <p className="text-sm text-gray-500 leading-relaxed">{current.description}</p>
          {current.tip && (
            <div className="mt-4 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2.5 text-left">
              <p className="text-xs text-amber-700">
                <span className="font-semibold">💡 İpucu: </span>{current.tip}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-5 mt-2 space-y-3">
          <div className="flex gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors text-sm"
              >
                ← Geri
              </button>
            )}
            <button
              onClick={isLast ? handleClose : () => setStep(s => s + 1)}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold rounded-xl transition-colors text-sm"
            >
              {isLast ? 'Başla →' : 'Sonraki →'}
            </button>
          </div>

          {isLast && (
            <label className="flex items-center justify-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={neverShow}
                onChange={e => setNeverShow(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-400">Bir daha gösterme</span>
            </label>
          )}
        </div>
      </div>
    </div>
  );
};
